"use client";
import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Box,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import "./video.css";
import { VideoServices } from "./VideoServices";
import Image from "next/image";

interface ServiceItem {
  id: number;
  cid: number;
  name: string;
  subHeading: string;
  amount: number;
  mrp: number;
  discount: number;
  coverImage: { secureHttpPath: string };
}

interface VideoBanner {
  bannerFile?: {
    secureHttpPath?: string;
  };
  videoFile?: {
    secureHttpPath?: string;
  };
  completeVideo?: {
    secureHttpPath?: string;
  };
  servicesList: ServiceItem[];
}
interface webImage {
  webImage: { secureHttpPath: string };
}
interface VideoSliderProps {
  videoBanners: any[];
  videoBanner: webImage;
}

const VideoSlider: React.FC<VideoSliderProps> = ({
  videoBanners,
  videoBanner,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [completeVideo, setCompleteVideo] = useState<VideoBanner | null>();
  const [cid, setCid] = useState<number>(0);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDialogOpen = (item: any) => {
    setCompleteVideo(item);
    setOpenDialog(true);
    setCid(item?.categoryId);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setCompleteVideo(null);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const videoElement = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            requestAnimationFrame(() => {
              videoElement
                .play()
                .catch((err) => console.warn("Video play error:", err));
            });
          } else {
            videoElement.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    videoRefs.current.forEach((video) => {
      if (video) observer.observe(video);
    });

    return () => observer.disconnect();
  }, [videoBanners]);

  return (
    <Box
      className="video-slider-shell"
      sx={{
        background:
          "linear-gradient(145deg, rgba(15, 23, 42, 0.98), rgba(35, 23, 52, 0.95) 58%, rgba(92, 54, 28, 0.9))",
        borderRadius: { xs: "22px", sm: "40px" },
        marginTop: 0,
        width: "min(100%, var(--salontym-content-width))",
        maxWidth: "100%",
        minWidth: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginBottom: "var(--salontym-section-gap)",
        padding: { xs: "0.75rem 0 0.85rem", sm: "1.35rem 0 1.6rem" },
        position: "relative",
        overflow: "hidden",
        zIndex: 0,
        boxShadow: "0 30px 80px rgba(15, 23, 42, 0.2)",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: -180,
          top: -220,
          background:
            "radial-gradient(circle, rgba(244, 114, 182, 0.24), rgba(244, 114, 182, 0))",
          width: "520px",
          height: "520px",
          borderRadius: "50%",
          zIndex: 1,
        }}
      ></Box>
      <Box
        sx={{
          position: "absolute",
          left: -120,
          bottom: -180,
          background:
            "radial-gradient(circle, rgba(250, 204, 21, 0.18), rgba(250, 204, 21, 0))",
          width: "420px",
          height: "420px",
          borderRadius: "50%",
          zIndex: 1,
        }}
      ></Box>
      {videoBanner?.webImage?.secureHttpPath && (
        <Box
          sx={{
            display: { xs: "block", sm: "none" },
            position: "absolute",
            overflow: "hidden",
            width: "55%",
            maxWidth: 280,
            height: 220,
            right: -16,
            top: -24,
            opacity: 0.12,
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Image
              src={videoBanner.webImage.secureHttpPath}
              alt=""
              fill
              sizes="280px"
              style={{ objectFit: "cover" }}
            />
          </Box>
        </Box>
      )}
      <Grid
        container
        item
        xs={12}
        margin="auto"
        sx={{ display: "block", position: "relative", zIndex: 11 }}
      >
        <Box
          sx={{
            paddingTop: "0.1rem",
            width: "100%",
            textAlign: "left",
            paddingInline: { xs: "0.85rem", sm: "1.5rem" },
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
          className="video-heading"
        >
          {/* Short static copy on mobile; full copy on sm+ */}
          <Typography
            className="video-eyebrow"
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              mb: { sm: 0.7 },
            }}
          >
            Beauty in motion
          </Typography>
          <Typography
            className="video-eyebrow"
            sx={{
              display: { xs: "inline-flex", sm: "none" },
              fontSize: "0.62rem",
              padding: "0.22rem 0.5rem",
              letterSpacing: "0.14em",
              mb: 0.45,
            }}
          >
            Reels
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              color: "#fff8f1",
              fontWeight: 700,
              display: { xs: "none", sm: "block" },
              fontSize: { sm: "2.2rem", md: "2.5rem", lg: "2.7rem" },
              lineHeight: { sm: 1.04 },
              letterSpacing: { sm: "-0.05em" },
              maxWidth: "18ch",
              textAlign: "left",
              margin: 0,
            }}
          >
            See beauty results before you book
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              color: "#fff8f1",
              fontWeight: 700,
              display: { xs: "block", sm: "none" },
              fontSize: "1.1rem",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              maxWidth: "22ch",
              textAlign: "left",
              margin: 0,
            }}
          >
            See results before you book
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 248, 241, 0.78)",
              display: { xs: "none", sm: "block" },
              fontSize: "1rem",
              lineHeight: 1.5,
              maxWidth: "36rem",
              mt: 0.55,
              textAlign: "left",
              margin: 0,
            }}
          >
            Browse quick service reels, real transformations, and tap to open
            bookable highlights.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 248, 241, 0.72)",
              display: { xs: "block", sm: "none" },
              fontSize: "0.75rem",
              lineHeight: 1.4,
              maxWidth: "32ch",
              mt: 0.35,
              textAlign: "left",
              margin: 0,
            }}
          >
            Tap a card to explore services.
          </Typography>
        </Box>
      </Grid>
      <Box
        className="video-card-rail"
        sx={{
          padding: {
            xs: "0.65rem 0.85rem 0.5rem",
            sm: "1rem 1.5rem 0.1rem",
          },
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(auto-fill, minmax(260px, 1fr))",
          },
          justifyContent: "center",
          alignItems: "stretch",
          gap: { xs: "0.65rem", sm: "1rem" },
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {videoBanners?.map((item, index) => (
          <motion.div
            key={item?.id ?? item?.categoryId ?? index}
            className="video-preview-card"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.22, delay: index * 0.04, ease: "easeOut" }}
            whileHover={{ y: isSmallScreen ? 0 : -4 }}
            style={{
              width: "100%",
              minWidth: 0,
              height: isSmallScreen ? "auto" : "390px",
              aspectRatio: isSmallScreen ? "240 / 312" : "300 / 390",
              zIndex: 10,
            }}
          >
            <Box
              className="video-preview-overlay"
              sx={{
                bottom: { xs: 10, sm: 14 },
                left: { xs: 10, sm: 14 },
                right: { xs: 10, sm: 14 },
                top: "auto",
              }}
            >
              <Typography
                className="video-preview-chip"
                sx={{ fontSize: { xs: "0.62rem", sm: "0.68rem" } }}
              >
                Tap to explore
              </Typography>
            </Box>
            <video
              id={`video-preview-${index}`}
              ref={(el) => {
                videoRefs.current[index] = el;
              }}
              autoPlay
              muted
              loop
              onClick={() => handleDialogOpen(item)}
              onContextMenu={(e) => e.preventDefault()}
              disablePictureInPicture
              controlsList="nodownload noremoteplayback noplaybackrate"
              controls={false}
              playsInline
              style={{
                borderRadius: isSmallScreen ? "20px" : "26px",
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: "pointer",
                display: "block",
              }}
            >
              <source src={item?.cover?.secureHttpPath} type="video/mp4" />
            </video>
          </motion.div>
        ))}
      </Box>
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="xs"
        aria-labelledby="video-dialog-title" // Add appropriate ARIA label
        aria-describedby="video-dialog-description" // Optional: Add description
        sx={{
          background: "transparent",
          "& .MuiDialog-paper": {
            maxWidth: { xs: "-webkit-fill-available", sm: "370px" },
            maxHeight: "100%",
          },
          "@media (max-width: 600px)": {
            "& .MuiDialog-paper": {
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              margin: 0,
            },
          },
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          {completeVideo?.completeVideo?.secureHttpPath ? (
            <>
              <IconButton
                onClick={handleDialogClose}
                sx={{
                  position: "absolute",
                  top: "calc(12px + env(safe-area-inset-top, 0px))",
                  left: "calc(12px + env(safe-area-inset-left, 0px))",
                  color: "#000",
                  background: "#fff",
                  borderRadius: "50%",
                  zIndex: 1012,
                  padding: "4px",
                  "&:hover": {
                    background: "#f3f7f4",
                  },
                }}
              >
                <KeyboardBackspaceIcon
                  sx={{
                    fontSize: "1.2rem",
                  }}
                />
              </IconButton>

              <video
                id="video-dialog-full"
                autoPlay
                muted={false}
                loop
                controls={false} // Disable video controls
                onContextMenu={(e) => e.preventDefault()}
                disablePictureInPicture
                controlsList="nodownload noremoteplayback noplaybackrate"
                playsInline
                style={{
                  width: "auto",
                  height: "100%",
                  objectFit: isSmallScreen ? "cover" : "contain",
                }}
              >
                <source
                  src={completeVideo?.completeVideo?.secureHttpPath}
                  type="video/mp4"
                />
              </video>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "-webkit-fill-available",
                  padding: "0px 8px",
                }}
              >
                <VideoServices completeVideo={completeVideo} cid={cid} />
              </Box>
            </>
          ) : (
            <Box sx={{ margin: "auto", position: "absolute" }}>
              <CircularProgress color="success" />
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default VideoSlider;
